import { Chip, IconButton, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  typography,
  spacing,
  colors,
} from "../../../../../../../../../assets/styles/tokens";
import RecentMonthTable from "./RecentMonthsTable";
import RecentWeeksTable from "./RecentWeeksTable";
import { useSelector } from "react-redux";
import { saveAs } from "file-saver";
import {
  checkIfMetricsExist,
  getValueForDashBoard,
} from "../../../../../helpers/businessleaderDashboardHelpers";
import {
  additionalQueriesrows02,
  additionalQueriestableHeadings02,
} from "../../../../../AdditionalQueries/AdditionalQueriesAccordion/AdditionalQueriesConstants";

const monthTableHeadings = {
  metric: "",
  actual_target: "Actual / Target",
  gap: "Gap",
  attainment: "Attainment",
};

const weeksTableHeadings = {
  metric: "",
  last_7_days: "Last 7 Days",
  days_7_14: "Days 7-14",
  trend: "Trend",
};

const RecentTable = () => {
  const { previousMonthQueryData, lastTwoWeeksQueryData } = useSelector(
    (state: any) => state.businessLeaderDashBoard,
  );
  const formatDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  const formatFullMonthLabel = (date: Date) => {
    return date.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  const formatShortDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}`;
  };

  const today = new Date();

  // 🔹 Previous Month
  const firstDayPrevMonth = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    1,
  );
  const lastDayPrevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
  // 🔹 Last 2 Weeks (ending 2 days ago)
  const endLastTwoWeeks = new Date();
  endLastTwoWeeks.setDate(today.getDate() - 2);

  const startLastTwoWeeks = new Date();
  startLastTwoWeeks.setDate(today.getDate() - 16);

  const previousMonthLabel = `${formatFullMonthLabel(
    firstDayPrevMonth,
  )} (${formatShortDate(firstDayPrevMonth)} - ${formatShortDate(
    lastDayPrevMonth,
  )})`;

  const lastTwoWeeksLabel = `${formatDate(
    startLastTwoWeeks,
  )} - ${formatDate(endLastTwoWeeks)}`;

  const formatPreviousMonthValueForCSV = (
    metricName: string,
    fieldName: "Actual" | "Target" | "Gap" | "Attainment",
    rawValue: any,
  ) => {
    if (rawValue === null || rawValue === undefined) {
      return "null";
    }
    if (fieldName === "Attainment") {
      const value = rawValue * 100;
      return Math.round(Number(value.toFixed(2))) + "%";
    }
    if (metricName === "CVR") {
      const value = rawValue * 100;
      return Math.round(Number(value.toFixed(2))) + "%";
    }
    if (metricName !== "FTD") {
      return Math.round(rawValue);
    }
    return Math.round(rawValue);
  };

  const handleDownloadMonthCSV = () => {
    if (!previousMonthQueryData?.query_output?.length) return;
    const headers = ["Metrics", "Actual", "Target", "Gap", "Attainment"];
    const csvRows = [headers.join(",")];
    previousMonthQueryData.query_output.forEach((item: any) => {
      const metric = item.Metrics;
      const formattedMetric = metric === "CVR" ? "CVR%" : metric;
      const row = [
        formattedMetric,
        formatPreviousMonthValueForCSV(metric, "Actual", item.Actual),
        formatPreviousMonthValueForCSV(metric, "Target", item.Target),
        formatPreviousMonthValueForCSV(metric, "Gap", item.Gap),
        formatPreviousMonthValueForCSV(metric, "Attainment", item.Attainment),
      ];
      csvRows.push(row.join(","));
    });
    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, "TargetVsActualPreviousMonth.csv");
  };

  const getWeeksMetricValueForCSV = (
    metricName: string,
    fieldName: string,
    dataSource: any,
  ) => {
    const data = getValueForDashBoard(metricName, dataSource) as any;
    let valueToReturn = null;
    if (data) {
      valueToReturn = data[fieldName];
    }
    if (
      valueToReturn !== null &&
      valueToReturn !== 0 &&
      fieldName !== "Metrics"
    ) {
      if (fieldName === "Trend") {
        valueToReturn = Math.round(Number(valueToReturn.toFixed(2)));
        return valueToReturn + "%";
      }
      if (metricName === "CVR") {
        valueToReturn = valueToReturn * 100;
        valueToReturn = Math.round(Number(valueToReturn.toFixed(2)));
        return valueToReturn + "%";
      }
      if (metricName !== "FTD") {
        valueToReturn = Math.round(valueToReturn);
        return valueToReturn + "$";
      }
      return Math.round(valueToReturn);
    }
    if (valueToReturn === null) {
      return "null";
    }
    return valueToReturn;
  };

  const handleDownloadWeeksCSV = () => {
    if (!lastTwoWeeksQueryData.query_output?.length) return;
    const headCells = Object.entries(additionalQueriestableHeadings02);
    const csvRows: string[] = [];
    const headers = headCells.map(([key]) => key);
    csvRows.push(headers.join(","));
    additionalQueriesrows02.forEach((row: any) => {
      if (checkIfMetricsExist(row.metric, lastTwoWeeksQueryData.query_output)) {
        const rowData: string[] = [];
        headers.forEach((key) => {
          const result = getWeeksMetricValueForCSV(
            row.metric,
            key,
            lastTwoWeeksQueryData.query_output,
          );
          if (result !== "null") {
            rowData.push(result);
          }
        });
        if (rowData.length > 0) {
          csvRows.push(rowData.join(","));
        }
      }
    });
    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, "TrendsOverPastFourteenDays.csv");
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mt: spacing.Spacing.xs.$value,
          mb: spacing.Spacing.xl.$value,
        }}
      >
        <Chip
          sx={{
            fontSize: typography.Size.Text.sm.$value,
            lineHeight: typography.Line_height.text.sm.$value,
            fontWeight: typography.Font_weight.Semibold.$value,
            borderRadius: spacing.Radius.md.$value,
            backgroundColor: colors.GrayLightMode[50].$value,
            border: "1px solid " + colors.GrayLightMode[300].$value,
            color: colors.GrayLightMode[500].$value,
          }}
          label={previousMonthLabel}
        />

        <IconButton
          aria-label=""
          color="default"
          size="large"
          data-tracker="648"
          onClick={handleDownloadMonthCSV}
        >
          <FileDownloadIcon sx={{ color: colors.GrayLightMode[700].$value }} />
        </IconButton>
      </Stack>

      <Typography
        sx={{
          fontWeight: typography.Font_weight.Medium.$value,
          color: colors.GrayLightMode[900].$value,
          my: 1,
        }}
      >
        Previous Month
      </Typography>
      <RecentMonthTable monthTableHeadings={monthTableHeadings} />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mt: spacing.Spacing["2xl"].$value,
          mb: spacing.Spacing.xl.$value,
        }}
      >
        <Chip
          sx={{
            fontSize: typography.Size.Text.sm.$value,
            lineHeight: typography.Line_height.text.sm.$value,
            fontWeight: typography.Font_weight.Semibold.$value,
            borderRadius: spacing.Radius.md.$value,
            backgroundColor: colors.GrayLightMode[50].$value,
            border: "1px solid " + colors.GrayLightMode[300].$value,
            color: colors.GrayLightMode[500].$value,
          }}
          label={lastTwoWeeksLabel}
        />

        <IconButton
          aria-label=""
          data-tracker="648"
          color="default"
          size="large"
          onClick={handleDownloadWeeksCSV}
        >
          <FileDownloadIcon sx={{ color: colors.GrayLightMode[700].$value }} />
        </IconButton>
      </Stack>
      <Typography
        sx={{
          fontWeight: typography.Font_weight.Medium.$value,
          color: colors.GrayLightMode[900].$value,
          my: 1,
        }}
      >
        Last 2 Weeks
      </Typography>
      <RecentWeeksTable weeksTableHeadings={weeksTableHeadings} />
    </>
  );
};

export default RecentTable;
